/**
 * URL utility functions for Exchanges page
 * Persists filtering, sorting, pagination, and open exchange in the hash
 */

import {
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
} from "../../Orders.PBH/config/sortOptions";
import { EXCHANGES_SEARCH_BY_KEYS } from "../constants/searchBy";
import { supportsSqlOnlyActionODataFilter } from "./sqlOnlyActionFilterUtils";

export interface ExchangesUrlParams {
  searchBy?: string;
  query?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  inboundStatus?: string;
  outboundStatus?: string;
  action?: string;
  exchangeRequestId?: string;
  skip?: number;
}

export interface ParsedExchangesUrl {
  searchBy: string;
  query: string | null;
  sortBy: string;
  sortOrder: "asc" | "desc";
  status: string | null;
  exchangeRequestId: string | null;
  skip: number;
}

/**
 * Parse getUrlVars() result into typed Exchanges state.
 * `action` is only honoured when it matches a toolbar dropdown value
 * (Waiting, Action, Open, Closed, Cancelled); invalid values are ignored.
 */
export function parseExchangesUrl(
  vars: Record<string, string>,
): ParsedExchangesUrl {
  const rawAction = vars.action?.trim() ?? "";
  const actionQuery = supportsSqlOnlyActionODataFilter(rawAction)
    ? rawAction
    : null;

  const searchBy = actionQuery
    ? "Actions"
    : EXCHANGES_SEARCH_BY_KEYS.find((key) => vars[key] != null) ??
      "CustomerPO";
  const query =
    searchBy === "Actions" ? actionQuery : (vars[searchBy] ?? null);
  let sortBy = DEFAULT_SORT_BY;
  let sortOrder = DEFAULT_SORT_ORDER as "asc" | "desc";
  if (vars.sort) {
    const [by, order] = vars.sort.split(",");
    if (by) sortBy = by;
    if (order === "asc" || order === "desc") sortOrder = order;
  }
  const status = vars.inboundStatus ?? vars.outboundStatus ?? null;
  const exchangeRequestId =
    vars.exchangeRequestId ?? vars.ExchangeRequestId ?? null;
  const skip = (() => {
    const s = vars.skip;
    if (s == null || s === "") return 0;
    const n = parseInt(s, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  })();

  return {
    searchBy,
    query,
    sortBy,
    sortOrder,
    status,
    exchangeRequestId,
    skip,
  };
}

const EXCHANGES_BASE = "#/exchanges";

/**
 * Build URL hash for Exchanges page with query parameters
 */
export function buildExchangesUrl(params: ExchangesUrlParams): string {
  const queryParams: string[] = [];

  if (params.exchangeRequestId) {
    queryParams.push(
      `exchangeRequestId=${encodeURIComponent(params.exchangeRequestId)}`,
    );
  }
  if (params.sortBy && params.sortOrder) {
    const isDefaultSort =
      params.sortBy === DEFAULT_SORT_BY &&
      params.sortOrder === DEFAULT_SORT_ORDER;
    if (!isDefaultSort) {
      queryParams.push(`sort=${params.sortBy},${params.sortOrder}`);
    }
  }
  if (params.skip != null && params.skip > 0) {
    queryParams.push(`skip=${params.skip}`);
  }
  if (params.inboundStatus) {
    queryParams.push(
      `inboundStatus=${encodeURIComponent(params.inboundStatus)}`,
    );
  } else if (params.outboundStatus) {
    queryParams.push(
      `outboundStatus=${encodeURIComponent(params.outboundStatus)}`,
    );
  }
  if (
    params.searchBy === "Actions" &&
    params.action &&
    supportsSqlOnlyActionODataFilter(params.action)
  ) {
    queryParams.push(`action=${encodeURIComponent(params.action)}`);
  } else if (params.searchBy && params.query) {
    queryParams.push(
      `${params.searchBy}=${encodeURIComponent(String(params.query))}`,
    );
  }

  return queryParams.length > 0
    ? `${EXCHANGES_BASE}?${queryParams.join("&")}`
    : EXCHANGES_BASE;
}

export interface LegacyExchangesRedirectTarget {
  pathname: string;
  search: string;
}

const LEGACY_EXCHANGES_PATHS = new Set([
  "/orders/pbh",
  "/orders/pbhnew",
  "/orders/pbnew",
]);

/**
 * True when the hash path is a legacy PBH route that should map to Exchanges.
 */
export function isLegacyExchangesPathname(pathname: string): boolean {
  const withoutHash = pathname.startsWith("#") ? pathname.slice(1) : pathname;
  const pathOnly = (withoutHash.split("?")[0] || "/").replace(/\/$/, "") || "/";
  return LEGACY_EXCHANGES_PATHS.has(pathOnly);
}

/**
 * Read the query portion from a hash URL (e.g. #/orders/pbh?foo=bar → ?foo=bar).
 */
export function extractSearchFromHash(hash: string): string {
  const qIndex = hash.indexOf("?");
  if (qIndex === -1) {
    return "";
  }
  return `?${hash.slice(qIndex + 1)}`;
}

/**
 * Normalize legacy PBH hash query strings for the Exchanges route.
 * Renames ExchangeRequestId (PascalCase) to exchangeRequestId (camelCase).
 * All other params are passed through unchanged.
 */
export function normalizeExchangesSearch(search: string): string {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  if (!raw) {
    return "";
  }

  const params = new URLSearchParams(raw);
  const legacyId = params.get("ExchangeRequestId");
  if (legacyId != null && !params.has("exchangeRequestId")) {
    params.set("exchangeRequestId", legacyId);
  }
  if (params.has("ExchangeRequestId")) {
    params.delete("ExchangeRequestId");
  }

  const normalized = params.toString();
  return normalized ? `?${normalized}` : "";
}

/**
 * Build a React Router redirect target for legacy PBH / mockup routes.
 * Preserves the full query string; falls back to window.location.hash when
 * HashRouter has not populated location.search yet.
 */
export function getLegacyExchangesRedirectTarget(location?: {
  search?: string;
}): LegacyExchangesRedirectTarget {
  const routerSearch = location?.search ?? "";
  const hashSearch =
    typeof window !== "undefined"
      ? extractSearchFromHash(window.location.hash || "")
      : "";

  return {
    pathname: "/exchanges",
    search: normalizeExchangesSearch(routerSearch || hashSearch),
  };
}

/**
 * Rewrite legacy PBH hash URLs to #/exchanges before React mounts.
 * Returns true when a rewrite was performed.
 */
export function rewriteLegacyExchangesHashIfNeeded(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const hash = window.location.hash || "";
  if (!hash.startsWith("#")) {
    return false;
  }

  const qIndex = hash.indexOf("?");
  const pathOnly = (qIndex === -1 ? hash.slice(1) : hash.slice(1, qIndex))
    .replace(/\/$/, "") || "/";

  if (!isLegacyExchangesPathname(pathOnly)) {
    return false;
  }

  const search = qIndex === -1 ? "" : hash.slice(qIndex);
  const nextHash = `#/exchanges${normalizeExchangesSearch(search)}`;
  if (hash === nextHash) {
    return false;
  }

  const { pathname, search: pageSearch } = window.location;
  window.location.replace(`${pathname}${pageSearch}${nextHash}`);
  return true;
}
